import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import './about.css';

const LOGO = `${process.env.PUBLIC_URL}/g2-logo.png`;

// ─────────────────────────────────────────────────────────────────
// JSON-LD structured data
// ─────────────────────────────────────────────────────────────────
const LD_ABOUT = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  url: 'https://groupardy.com/about',
  mainEntity: {
    '@type': 'Organization',
    name: 'Groupardy',
    url: 'https://groupardy.com/',
    logo: 'https://groupardy.com/g2-logo.png',
    founder: { '@type': 'Person', name: 'JT Burgess', url: 'https://www.jtburgess.com' },
    description: 'Groupardy is an AI-powered Jeopardy-style trivia game show for groups. The board runs on a shared screen, players join from their phones, and an AI host reads clues, judges answers, and narrates the night.',
  },
});

const About = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="about">
      <Helmet>
        <title>About Groupardy · A trivia game show where everyone answers every clue</title>
        <meta
          name="description"
          content="Why we built Groupardy: buzz-in trivia turns 18 of 20 people into spectators. We made a game show where everyone types every answer on their phone — and a host who never has to sit out and read."
        />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href="https://groupardy.com/about" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://groupardy.com/about" />
        <meta property="og:title" content="About Groupardy · The story behind the AI-hosted trivia game show" />
        <meta
          property="og:description"
          content="Built because buzz-in trivia leaves most of the room watching. Groupardy lets everyone answer every clue, with an AI host so no friend has to sit out."
        />
        <meta property="og:site_name" content="Groupardy" />
        <meta property="og:image" content="https://groupardy.com/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About Groupardy" />
        <meta
          name="twitter:description"
          content="A Jeopardy-style trivia game show where everyone in the room answers every clue. No buzz-in race, no spectators, no friend stuck reading."
        />
        <meta name="twitter:image" content="https://groupardy.com/og-image.jpg" />
        <meta name="theme-color" content="#1a1a2e" />
        <script type="application/ld+json">{LD_ABOUT}</script>
      </Helmet>

      {/* ─── NAV ─── */}
      <header className="ab-nav">
        <div className="ab-wrap ab-nav-inner">
          <a className="ab-nav-brand" href="/" aria-label="Groupardy home">
            <img src={LOGO} alt="Groupardy logo" className="ab-nav-logo" />
            <span className="ab-nav-wordmark">Groupardy</span>
          </a>
          <nav className="ab-nav-links" aria-label="Primary">
            <a className="ab-nav-link" href="/#how">How it works</a>
            <a className="ab-nav-link" href="/#host">AI host</a>
            <a className="ab-nav-link" href="/#faq">FAQ</a>
            <a className="ab-nav-link" href="/about" aria-current="page">About</a>
          </nav>
          <div className="ab-nav-actions">
            <a className="ab-nav-signin" href="/login">Sign in</a>
            <a className="ab-btn ab-btn-primary ab-btn-sm" href="/create">Start a game</a>
          </div>
        </div>
      </header>

      <main>
        {/* ─── HERO ─── */}
        <section className="ab-hero" aria-labelledby="about-title">
          <div className="ab-wrap ab-hero-inner">
            <div className="ab-hero-eyebrow">Our story</div>
            <h1 id="about-title">Built so the whole room plays — not just whoever buzzes first.</h1>
            <p className="ab-lead">
              Buzz-in trivia turns 18 of 20 people into spectators. We wanted a game show where
              every clue is answered by everyone in the room, every time — and where the friend
              hosting it gets to play too.
            </p>
          </div>
        </section>

        {/* ─── ORIGIN ─── */}
        <section className="ab-section ab-origin-band">
          <div className="ab-wrap ab-origin-grid">
            <div className="ab-origin-copy">
              <div className="ab-section-eyebrow">Why we built it</div>
              <h2>If most of the room can't answer, it stops being a game.</h2>
              <p>
                You've been at that game night. There are eighteen people in the room. The host
                reads a question. Three people lunge for the buzzer. One of them gets it. The other
                fifteen — most of the table — sit on their hands waiting for the next clue, hoping
                it's a topic they know.
              </p>
              <p>
                Two rounds in, half the room is on their phones for real reasons. The "game" has
                turned into a duel between two trivia diehards while the rest of the table nods
                politely. And whoever volunteered to host is out of it too, because they're reading
                the questions instead of answering them.
              </p>
              <p>
                We wanted a real game show — <strong>a board, a host, wagers, a Final
                Jeopardy</strong> — designed for the table you're already at, where every single
                person answers every single clue. Not a quiz. A game show where nobody is waiting.
              </p>
            </div>

            <aside className="ab-pullquote" aria-label="Founding insight">
              <div className="ab-pullquote-label">The insight</div>
              <p className="ab-pullquote-text">
                "Buzz-in trivia makes eighteen of twenty people spectators. We thought that was the
                bug, not the feature."
              </p>
              <div className="ab-pullquote-attr">
                <span className="ab-pq-dot" />
                <span>JT Burgess · founder</span>
              </div>
            </aside>
          </div>
        </section>

        {/* ─── PRINCIPLES / HOW IT'S DIFFERENT ─── */}
        <section className="ab-section">
          <div className="ab-wrap">
            <div className="ab-section-head ab-center">
              <div className="ab-section-eyebrow">What we believed</div>
              <h2>Three calls we made early, and never walked back.</h2>
              <p className="ab-sub">
                We didn't set out to build a quiz app. We set out to build the night you keep
                bringing up the following Tuesday.
              </p>
            </div>
            <div className="ab-principles">
              <div className="ab-principle">
                <div className="ab-principle-num">01</div>
                <h3>Everyone answers every clue</h3>
                <p>
                  No buzzer race. Every phone in the room types every answer, in parallel. Speed and
                  accuracy both matter, but nobody is locked out of a question because someone else
                  reacted faster. Twenty people stay in the game for forty clues straight.
                </p>
              </div>
              <div className="ab-principle">
                <div className="ab-principle-num">02</div>
                <h3>The host is software</h3>
                <p>
                  A real game show host reads with timing, judges fairly, doesn't get bored, and
                  doesn't lose their place. AI can do all four. That frees the human who would have
                  volunteered to host — they're back in the game like everyone else.
                </p>
              </div>
              <div className="ab-principle">
                <div className="ab-principle-num">03</div>
                <h3>Every board is fresh</h3>
                <p>
                  Pre-written question packs go stale by round three of the night and round two of
                  the year. Generating new categories on demand means your office onboarding board
                  and your bachelorette board both exist, and neither has ever been played before.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ─── FOUNDER ─── */}
        <section className="ab-section ab-founder-band">
          <div className="ab-wrap">
            <div className="ab-founder">
              <div className="ab-avatar" aria-hidden="true">JT</div>
              <div>
                <div className="ab-founder-eyebrow">Who's behind it</div>
                <h3>JT Burgess</h3>
                <div className="ab-founder-role">Founder · designer · writer of way too many sample clues</div>
                <p>
                  Groupardy is a one-person shop right now. JT has spent his career building software
                  for groups — collaboration tools, social platforms, the boring infrastructure
                  underneath the fun parts — and figured the world had room for one more thing that
                  makes a Wednesday night feel like an occasion.
                </p>
                <p>
                  Every question template, every host voice, every wager animation: someone wrote
                  it. That someone has opinions. The main one: game nights are good for people, and
                  the irony of using AI to make more of them happen — software writing the boards,
                  reading the clues, judging the answers, so the humans in the room get to actually
                  play — is, to me, exactly the right use of the technology.
                </p>
                <p>
                  Most takes on AI right now are bleak. This one is simpler. If something I built
                  makes a room of people laugh harder and stay later, that's about as fulfilled as
                  I get. Have a great game night.
                </p>
                <div className="ab-founder-links">
                  <a href="https://www.jtburgess.com" target="_blank" rel="noopener noreferrer">jtburgess.com</a>
                  <a href="mailto:jt@groupardy.com">jt@groupardy.com</a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ─── NEXT / EARLY ACCESS ─── */}
        <section className="ab-section ab-next-band">
          <div className="ab-wrap">
            <div className="ab-section-head ab-center">
              <div className="ab-section-eyebrow">Where this is going</div>
              <h2>We're in early access. Come play while it's free.</h2>
              <p className="ab-sub">
                Groupardy works today. It's also evolving fast — every game played teaches the host
                something, every weekend ships a new feature. Here's where we are, and where we're
                pointed.
              </p>
            </div>
            <div className="ab-next-grid">
              <div className="ab-next-card">
                <span className="ab-tag ab-tag-gold">Right now</span>
                <h3>Please send me your feedback.</h3>
                <p>
                  Groupardy is being built and shaped by the people actually playing it. If you
                  host a game — good night, weird night, broken night, surprising night — tell me.
                  I'm a real person, not an AI form, not a ticket queue. I read every email, I
                  reply, and the good ideas land in the next version.
                </p>
                <div className="ab-feedback-cta">
                  <a className="ab-btn ab-btn-primary ab-btn-sm" href="mailto:jt@groupardy.com">
                    Email me · jt@groupardy.com
                  </a>
                </div>
                <p className="ab-feedback-sig">— JT, founder</p>
              </div>

              <div className="ab-next-card">
                <span className="ab-tag">What's next</span>
                <h3>What we're working on</h3>
                <p>
                  We're working on the stuff hosts have asked for most: saved boards you can replay,
                  team play modes, more host voices, and recap cards built for the group chat.
                </p>
                <ul className="ab-next-list">
                  <li><span className="ab-v">✓</span> Saved boards and reusable category libraries</li>
                  <li><span className="ab-v">✓</span> Team mode for offices and classrooms</li>
                  <li><span className="ab-v">✓</span> Voice variety — late-night, warm, classic, and weirder</li>
                  <li><span className="ab-v">✓</span> Shareable recap cards and post-game superlatives</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ─── FINAL CTA ─── */}
        <section className="ab-final-cta">
          <div className="ab-wrap ab-final-cta-inner">
            <div className="ab-section-eyebrow">Your move</div>
            <h2>Bring the room. We'll bring the board.</h2>
            <p className="ab-sub">
              Two minutes from now the board is on the TV, every phone is in, and the host is
              reading the first clue — to the whole room, including whoever usually gets stuck
              reading it.
            </p>
            <div className="ab-final-cta-row">
              <a className="ab-btn ab-btn-primary ab-btn-lg" href="/create">Start a game</a>
            </div>
            <div className="ab-final-trust">
              <span>Free during early access</span>
              <span>·</span>
              <span>No download</span>
              <span>·</span>
              <span>Up to 100 players</span>
            </div>
          </div>
        </section>
      </main>

      {/* ─── FOOTER ─── */}
      <footer className="ab-footer">
        <div className="ab-wrap">
          <div className="ab-foot-row">
            <div className="ab-foot-brand">
              <img src={LOGO} alt="" style={{ width: 28, height: 28 }} />
              Groupardy
            </div>
            <nav className="ab-foot-links" aria-label="Footer">
              <a href="/">Home</a>
              <a href="/create">Start a game</a>
              <a href="/about" aria-current="page">About</a>
              <a href="/blog">Blog</a>
              <a href="/contact">Contact</a>
              <a href="/terms">Terms</a>
              <a href="/privacy">Privacy</a>
            </nav>
          </div>
          <div className="ab-foot-bar">© 2026 Groupardy. Made for the group chat that always says "we should hang out more".</div>
        </div>
      </footer>
    </div>
  );
};

export default About;
